import { useEffect } from 'react';
import type {
  Repository,
  RepositoryBranch,
  RepositoryOwner,
  RepositoryProject,
  ScmProviderDescriptor,
} from '../../api/types';
import { RepoOptionSelect } from './RepoOptionSelect';
import { useRepositoryOptions } from './useRepositoryOptions';

export interface RepoHierarchyValue {
  owner: RepositoryOwner | null;
  project: RepositoryProject | null;
  repository: Repository | null;
  branch: RepositoryBranch | null;
}
export interface RepoHierarchyPickerProps {
  provider: ScmProviderDescriptor;
  value: RepoHierarchyValue;
  onChange: (value: RepoHierarchyValue) => void;
  disabled?: boolean;
}

function useDefaultSelections(
  props: RepoHierarchyPickerProps,
  options: ReturnType<typeof useRepositoryOptions>,
) {
  const { value, onChange } = props;
  useEffect(() => {
    if (options.owners.items.length === 1 && !value.owner) {
      onChange({
        owner: options.owners.items[0],
        project: null,
        repository: null,
        branch: null,
      });
    }
  }, [options.owners.items, value, onChange]);
  useEffect(() => {
    if (!value.branch) {
      const branch =
        options.branches.items.find(item => item.isDefault) ??
        options.branches.items[0];
      if (branch) onChange({ ...value, branch });
    }
  }, [options.branches.items, value, onChange]);
}

/** Provider-aware hierarchy; every request exposes failure/empty/retry separately. */
export function RepoHierarchyPicker(
  props: RepoHierarchyPickerProps,
): JSX.Element {
  const { provider, value, onChange, disabled } = props;
  const options = useRepositoryOptions(props);
  useDefaultSelections(props, options);
  return (
    <>
      <RepoOptionSelect
        id="repo-owner"
        label={provider.hierarchy.ownerLabel}
        query={options.owners}
        value={value.owner?.id ?? ''}
        optionId={item => item.id}
        optionLabel={item => item.name}
        disabled={disabled}
        hidden={options.owners.items.length === 1}
        onSelect={owner =>
          onChange({ owner, project: null, repository: null, branch: null })
        }
      />
      {provider.hierarchy.hasProject && (
        <RepoOptionSelect
          id="repo-project"
          label={provider.hierarchy.projectLabel ?? 'Project'}
          query={options.projects}
          value={value.project?.id ?? ''}
          optionId={item => item.id}
          optionLabel={item => item.name}
          disabled={disabled}
          onSelect={project =>
            onChange({ ...value, project, repository: null, branch: null })
          }
        />
      )}
      <RepositoryAndBranch props={props} options={options} />
    </>
  );
}

function RepositoryAndBranch({
  props,
  options,
}: {
  props: RepoHierarchyPickerProps;
  options: ReturnType<typeof useRepositoryOptions>;
}) {
  const { value, onChange, disabled } = props;
  return (
    <>
      <RepoOptionSelect
        id="repo-repository"
        label="Repositório"
        query={options.repositories}
        value={value.repository?.externalId ?? ''}
        optionId={item => item.externalId}
        optionLabel={item => item.name}
        disabled={disabled}
        onSelect={repository =>
          onChange({ ...value, repository, branch: null })
        }
      />
      <RepoOptionSelect
        id="repo-branch"
        label="Branch"
        query={options.branches}
        value={value.branch?.name ?? ''}
        optionId={item => item.name}
        optionLabel={item =>
          `${item.name}${item.isDefault ? ' (default)' : ''}`
        }
        disabled={disabled}
        onSelect={branch => onChange({ ...value, branch })}
      />
    </>
  );
}
